import { CircleCheckBig, CircleX, MessageCircleQuestion, OctagonAlert, RotateCcw, Stamp } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { useRetryTask } from "../../hooks/use-actions";
import { isDraftPullRequest, isOpenTask, issueKey, needsApproval } from "../../lib/tasks";
import { useFactory } from "../../store";
import type { FactoryState, Run, Task } from "../../types";
import { MergeActions } from "../merge-ticket";
import { EmptyState } from "../page";
import { TimeAgo } from "../time-ago";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from "../ui/item";

/** Everything on the floor that is waiting for a person: approvals, questions, blockers and failures. */
export function AttentionList({ state }: { state: FactoryState }) {
  const { agentById } = useFactory();
  const tasks = new Map(state.tasks.map((t) => [t.id, t]));
  const runsById = new Map(state.runs.map((r) => [r.id, r]));
  const approvals = state.mergeQueue.filter((m) => needsApproval(m));
  const input = state.runs.filter((r) => r.status === "waiting_for_input");
  const blocked = state.runs.filter((r) => r.status === "blocked");
  const failed = state.tasks.filter((t) => {
    const run = t.currentRunId ? runsById.get(t.currentRunId) : undefined;
    return isOpenTask(t) && (run?.status === "failed" || run?.status === "expired");
  });
  const total = approvals.length + input.length + blocked.length + failed.length;
  const agentName = (run: Run) => agentById.get(run.agentId)?.name ?? run.agentId;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Needs attention</CardTitle>
        <CardDescription>Work orders that are waiting on a person</CardDescription>
        {total > 0 && (
          <CardAction>
            <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300">{total} open</Badge>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <EmptyState
            icon={CircleCheckBig}
            title="All clear"
            description="No approvals, questions or blockers right now."
            className="py-8"
          />
        ) : (
          <ItemGroup className="gap-2">
            {approvals.map((item) => (
              <Row
                key={item.id}
                icon={<Stamp />}
                tone="amber"
                task={tasks.get(item.taskId)}
                taskId={item.taskId}
                detail={
                  <>
                    PR #{item.prNumber} is {isDraftPullRequest(item) ? "a draft" : "ready for review"} ·{" "}
                    <TimeAgo at={item.updatedAt} />
                  </>
                }
                action={<MergeActions item={item} />}
              />
            ))}
            {input.map((run) => (
              <Row
                key={run.id}
                icon={<MessageCircleQuestion />}
                tone="violet"
                task={tasks.get(run.taskId)}
                taskId={run.taskId}
                detail={
                  <>
                    {agentName(run)} is asking for input · <TimeAgo at={run.updatedAt} />
                  </>
                }
                action={
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/tasks/${run.taskId}`}>Reply</Link>
                  </Button>
                }
              />
            ))}
            {blocked.map((run) => (
              <Row
                key={run.id}
                icon={<OctagonAlert />}
                tone="amber"
                task={tasks.get(run.taskId)}
                taskId={run.taskId}
                detail={`${agentName(run)}: ${run.blockedReason ?? "blocked"}`}
                action={
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/tasks/${run.taskId}`}>View</Link>
                  </Button>
                }
              />
            ))}
            {failed.map((task) => {
              const run = task.currentRunId ? runsById.get(task.currentRunId) : undefined;
              return (
                <Row
                  key={task.id}
                  icon={<CircleX />}
                  tone="red"
                  task={task}
                  taskId={task.id}
                  detail={`${run ? agentName(run) : "Run"} failed${run?.error ? `: ${run.error}` : ""}`}
                  action={<RetryButton taskId={task.id} agentId={run?.agentId} />}
                />
              );
            })}
          </ItemGroup>
        )}
      </CardContent>
    </Card>
  );
}

const TONE: Record<"amber" | "violet" | "red", string> = {
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  red: "bg-red-500/10 text-red-600 dark:text-red-400",
};

function Row({
  icon,
  tone,
  task,
  taskId,
  detail,
  action,
}: {
  icon: ReactNode;
  tone: keyof typeof TONE;
  task: Task | undefined;
  taskId: string;
  detail: ReactNode;
  action: ReactNode;
}) {
  const key = task ? issueKey(task) : null;
  return (
    <Item variant="outline" size="sm">
      <ItemMedia className={cn("size-8 rounded-md [&_svg]:size-4", TONE[tone])}>{icon}</ItemMedia>
      <ItemContent className="min-w-0">
        <ItemTitle className="w-full min-w-0">
          <Link to={`/tasks/${taskId}`} className="flex min-w-0 items-center gap-2 hover:underline">
            {key && <span className="shrink-0 font-mono text-xs text-muted-foreground">{key}</span>}
            <span className="truncate">{task?.title ?? "Work order"}</span>
          </Link>
        </ItemTitle>
        <ItemDescription className="line-clamp-1">{detail}</ItemDescription>
      </ItemContent>
      <ItemActions>{action}</ItemActions>
    </Item>
  );
}

function RetryButton({ taskId, agentId }: { taskId: string; agentId: string | undefined }) {
  const retry = useRetryTask(taskId);
  return (
    <Button size="sm" variant="outline" onClick={() => retry.mutate({ agentId })} disabled={retry.isPending}>
      <RotateCcw /> Retry
    </Button>
  );
}
